import React, { useState, useEffect } from "react";
import classNames from "classnames";
import { MDBSpinner } from "mdb-react-ui-kit";
import { TwitterTweetEmbed } from "react-twitter-embed";
import { SectionProps } from "../../../utils/SectionProps";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Cell,
} from "recharts";
import dayjs from "dayjs";
import { getSimpleConfig } from "../../../utils/Client";

const propTypes = {
  ...SectionProps.types,
};

const defaultProps = {
  ...SectionProps.defaults,
};

// Array of colors for the bars
const colors = [
  "#8884d8", // Purple
  "#82ca9d", // Green
  "#ffc658", // Yellow
  "#ff8042", // Orange
  "#0088fe", // Blue
  "#00c49f", // Teal
  "#ffbb28", // Light Yellow
  "#ff8042", // Orange
];

const KeywordTrendChart = ({ range = "week", data = [] }) => {
  const now = dayjs();
  const cutoff =
    range === "month" ? now.subtract(30, "day") : now.subtract(7, "day");
  const subtext = range === "month" ? "Last 30 days" : "Last 7 days";

  // Step 1: Filter and group by keyword
  const keywordCounts = data.reduce((acc, item) => {
    // Parse date correctly by splitting and reordering
    const [day, month, year] = item.date.split("-");
    const itemDate = dayjs(`${year}-${month}-${day}`);
    const isAfterCutoff =
      itemDate.isAfter(cutoff) || itemDate.isSame(cutoff, "day");

    if (isAfterCutoff) {
      acc[item.keyword] = (acc[item.keyword] || 0) + 1;
    }
    return acc;
  }, {});

  // Step 2: Convert to chart-friendly format
  const chartData = Object.entries(keywordCounts).map(
    ([keyword, count], index) => ({
      keyword,
      count,
      color: colors[index % colors.length],
    }),
  );

  return (
    <div style={{ width: "100%", height: 300 }}>
      <h4>Trending Keywords ({subtext})</h4>
      <ResponsiveContainer>
        <BarChart
          data={chartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="keyword"
            angle={-45}
            textAnchor="end"
            height={80}
            interval={0}
          />
          <YAxis />
          <Tooltip />
          <Bar dataKey="count" fill="#8884d8">
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

function Qodo({
  className,
  topOuterDivider,
  bottomOuterDivider,
  topDivider,
  bottomDivider,
  hasBgColor,
  invertColor,
  ...props
}) {
  const [tweetData, setTweetData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.title = "Qodo | Tweets Analytics";
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getSimpleConfig("codium-tweet-history");
        // Sort the response by date in descending order (newest first)
        const sortedData = response.sort((a, b) => {
          const [dayA, monthA, yearA] = a.date.split("-");
          const [dayB, monthB, yearB] = b.date.split("-");
          const dateA = new Date(`${yearA}-${monthA}-${dayA}`);
          const dateB = new Date(`${yearB}-${monthB}-${dayB}`);
          return dateB - dateA;
        });
        setTweetData(sortedData);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const TweetDisplay = ({ tweetUrl }) => {
    const tweetId = tweetUrl.split("/").pop(); // extract ID from URL

    return (
      <div>
        <TwitterTweetEmbed tweetId={tweetId} />
      </div>
    );
  };

  const outerClasses = classNames(
    "hero section center-content",
    topOuterDivider && "has-top-divider",
    bottomOuterDivider && "has-bottom-divider",
    hasBgColor && "has-bg-color",
    invertColor && "invert-color",
    className,
  );

  if (loading)
    return (
      <div className="text-center" style={{ padding: "2rem" }}>
        <MDBSpinner color="primary">
          <span className="visually-hidden">Loading...</span>
        </MDBSpinner>
      </div>
    );
  if (error) return <div>Error: {error}</div>;

  return (
    <section {...props} className={outerClasses} style={{ padding: "0 1rem" }}>
      <div className="hero-content">
        <h2>
          Tweet analytics for <span className="text-color-primary">Qodo</span>
        </h2>
      </div>
      <div
        className="container-sm"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
          gap: "2rem",
          width: "100%",
          maxWidth: "1400px",
          margin: "0 auto",
          padding: "0",
        }}
      >
        <KeywordTrendChart range="week" data={tweetData} />
        <KeywordTrendChart range="month" data={tweetData} />
      </div>
      <br />
      <h3>
        Recent Tweets from our{" "}
        <span className="text-color-primary">Keywords</span>
      </h3>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
          gap: "1rem",
          width: "100%",
          maxWidth: "1400px",
          margin: "0 auto",
          padding: "1rem",
        }}
      >
        {tweetData.slice(0, 8).map((tweet, index) => (
          <div key={index} style={{ width: "100%", minWidth: "280px" }}>
            <TweetDisplay tweetUrl={tweet.link} />
          </div>
        ))}
      </div>
    </section>
  );
}

Qodo.propTypes = propTypes;
Qodo.defaultProps = defaultProps;
export default Qodo;
